import React, {useState, useEffect, useRef, useLayoutEffect} from 'react';
import {IOutLinks} from "../api/match-center"
import {fetchFeaturedEventsData, IFeaturedEventsResponse} from '../api/general'
import bn from "../utils/bemnames";
import {_t} from '../utils/translations';
import {
  EventStatus,
  IChannel,
  IEventCompetitor,
  IFeaturedEvent,
  IMaxOdd,
  IProviderCompetition,
  IWidgetConfiguration,
  IWidgetProvider,
  oddsFormats,
  widgetConfigKeys
} from '../utils/types';
import LoadingBlock from './LoadingBlock';
import {DateTime} from 'luxon';
import CountdownTimer from './CountdownTimer';
import {removeLocationFromChannelName} from '../utils/featured-events';
import {getOutLink, isItalianOddsAvailable} from '../utils/odds';
import {slugify} from '../utils/helpers';
import {getChannelOutLink} from '../utils/channels';
import useDraggableScroll from './useDraggableScroll';
import {getEventLink, isInNextHours} from '../utils/event';
import EmptyComponent from "./EmptyComponent/EmptyComponent";
import {useGlobalState} from "../contexts/GlobalState";
import Odds from "./Standings/Odds";
import OddsButtonWithOperatorOnly from "./Buttons/OddsButtonWithOperatorOnly";
import {isItalianMarket} from "../utils/regions";
import OddsTrackingLink from './OddsTrackingLink';
import ChannelTrackingLink from './ChannelTrackingLink';

const bem = bn.create('featured-events');

const DIRECTION_RIGHT = 'right';
const DIRECTION_LEFT = 'left';
const AUTOSCROLL_SPEED = 40; //ms

interface IProps {
  componentName: string
  eventID: number
  languageID: number
  isMobile: boolean
  oddsFormat: oddsFormats
  widgetId: number
  builderComponentId: number
  outLinks: IOutLinks | null
  allowedProviders: IWidgetProvider[]
  linkTemplate: string | null,
  isLeagueCenter: boolean
}

const FeaturedEvents: React.FC<IProps> = (props) => {
  const {
    componentName,
    eventID,
    languageID,
    allowedProviders,
    isMobile = false,
    oddsFormat,
    outLinks,
    builderComponentId,
    widgetId,
    linkTemplate,
    isLeagueCenter
  } = props;

  const [loading, setLoading] = useState<Boolean>(false)
  const [requestFailed, setRequestFailed] = useState<Boolean>(false)
  const [featuredEventsData, setFeaturedEventsData] = useState<IFeaturedEventsResponse | null>(null)
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMouseOver, setMouseOver] = useState<Boolean>(false);
  const [scrollDirection, setScrollDirection] = useState<string>(DIRECTION_RIGHT);
  const [dimension, setDimension] = useState<number>(0);
  const {globalState} = useGlobalState()

  const promotedProvider: IProviderCompetition | null = globalState.promotedProvider;

  const MAX_CHANNELS_TO_SHOW_ON_TOP_BAR = 2;
  let featuredEventsRequest = `,
    "languageId": ${languageID},
    "widgetId": ${widgetId},
    "allowedProvidersIds": [${allowedProviders.map((provider) => provider.providerId)}]}`

  if(!isLeagueCenter) {
    featuredEventsRequest = ` { "eventId": ${eventID}` + featuredEventsRequest
  }else{
    featuredEventsRequest = ` { "competitionId": ${eventID}` + featuredEventsRequest
  }

  featuredEventsRequest = JSON.parse(featuredEventsRequest);

  const updateLiveData = (r: IFeaturedEventsResponse) => {
    const updateEventData = async (initialResponse: IFeaturedEventsResponse) => {
      try {
        const r = await fetchFeaturedEventsData(featuredEventsRequest);
        if (r) {
          //update banner data
          setFeaturedEventsData(r)
        }
      } catch (err) {
        // catches errors both in fetch and r
        console.error(err);
        setRequestFailed(true)
      } finally {
        // do it again in 3 seconds if there's a match that is still in progress
        const liveEvents = r.featuredEvents.filter(event => event.status === EventStatus.Inprogress);
        if (liveEvents.length > 0) {
          setTimeout(function () {
            updateEventData(initialResponse);
          }, 60000)
        }
      }
    };
    //only start the update live data loop if there is an event in progress on first load
    const liveEvents = r.featuredEvents.filter(event => event.status === EventStatus.Inprogress);
    if (liveEvents.length > 0) {
      updateEventData(r)
    }
  }

  const getFeaturedEventsData = () => {
    setLoading(true);
    setFeaturedEventsData(null)
    fetchFeaturedEventsData
      (featuredEventsRequest).then((r: IFeaturedEventsResponse) => {
        setFeaturedEventsData(r)
        updateLiveData(r)
        setLoading(false);
        ref?.current?.addEventListener('mouseenter', () => {
          setMouseOver(true);
        });
        ref?.current?.addEventListener('mouseleave', () => {
          setMouseOver(false);
        });
      }).catch(() => {
        setRequestFailed(true)
        setLoading(false)
      })
  }

  const filterOdds = (odds: IMaxOdd[]): IMaxOdd[] => {
    if (promotedProvider === null)
      return odds;
    let oddsItems = odds.filter((item) => {
      return item.providerId === promotedProvider?.providerId;
    });

    if (oddsItems === undefined)
      return [];
    return oddsItems;
  }

  // eslint-disable-next-line
  useEffect(getFeaturedEventsData, [eventID, languageID, allowedProviders]);

  useEffect(() => {
    let scrollInterval: NodeJS.Timeout | null = null;
    const autoScroll = () => {
      if (ref.current) {
        if (scrollDirection === DIRECTION_RIGHT) {
          if ((ref.current.scrollWidth - ref.current.clientWidth) > ref.current.scrollLeft)
            ref.current.scrollLeft += 1;
          else setScrollDirection(DIRECTION_LEFT);
        } else {
          if (ref.current.scrollLeft !== 0) {
            ref.current.scrollLeft -= 1;
          } else setScrollDirection(DIRECTION_RIGHT);
        }
      }
    }

    if (isMouseOver) {
      if (scrollInterval)
        clearInterval(scrollInterval);
    } else {
      if (scrollInterval)
        clearInterval(scrollInterval);
      scrollInterval = setInterval(autoScroll, AUTOSCROLL_SPEED);
    }

    return () => {
      if (scrollInterval)
        clearInterval(scrollInterval);
    }
  }, [isMouseOver, scrollDirection]);

  useLayoutEffect(() => {
    const getWidgetSize = () => {
      if(containerRef.current) {
        setDimension(containerRef.current.offsetWidth);
      }
    }
    getWidgetSize();
    window.addEventListener('resize', () => {
      getWidgetSize()
    });
    return () => {
      window.removeEventListener('resize', getWidgetSize);
    }

  }, []);

  const {onMouseDown} = useDraggableScroll(ref, {direction: 'horizontal'});

  //check for touchDevices as onMouseDown doesn't work with touch devices
  const isTouchDevice = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));

  const broadcastLinksDisabled = globalState?.widget?.configuration?.find((item: IWidgetConfiguration) => item.key === widgetConfigKeys.notBroadcast);
  const hideChannelLinks = globalState?.widget?.configuration?.find((item: IWidgetConfiguration) => item.key === widgetConfigKeys.hideChannelLinks)?.value;

  if (requestFailed && !featuredEventsData) {
    return <EmptyComponent componentName={componentName} />
  }

  const checkOddsAvailability = (event: IFeaturedEvent) => {
    return promotedProvider !== null && filterOdds(event.maxOdds).length === 0 && !(isItalianMarket(globalState.widget?.client.regionIds) && !isItalianOddsAvailable(event.maxOdds))
  }
  const checkIfItalianMarketAndHaveOdds = (event: IFeaturedEvent) => {
    return (isItalianMarket(globalState.widget?.client.regionIds) && !isItalianOddsAvailable(event.maxOdds))
  }

  return (
    <div
      ref={containerRef}
    >
      {loading ? <LoadingBlock height={120} heightMobile={120} isMobile={isMobile} /> : (
        <div
          style={{width: `${dimension}px`, overflow: 'hidden'}}
          className={isTouchDevice ? bem.e('wrapper-touch') : bem.e('wrapper')}
          ref={ref}
          onMouseDown={onMouseDown}
        >
          {featuredEventsData && featuredEventsData.featuredEvents.map((event: IFeaturedEvent, index: number) => {
            return (
              <div key={index} className={bem.e('event-wrapper')}>
                <div className={`${bem.e('top-bar')} ${event.status === EventStatus.Inprogress ? bem.e('top-bar-live') : ''}`}>
                  {event.status !== EventStatus.Inprogress ? (
                    <div className={bem.e('top-bar__date-time')}>
                      {event.userStartTime && isInNextHours(event.userStartTime) ?
                        <CountdownTimer isMobile={isMobile} eventDate={event.userStartTime} returnPlainText={true} /> : (
                          <>
                            {DateTime.fromISO(event.userStartTime).toLocaleString({
                              day: 'numeric',
                              month: 'numeric',
                            })} {DateTime.fromISO(event.userStartTime).toLocaleString({
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                          </>
                        )}
                    </div>
                  ) : (
                    <div className={bem.e('top-bar__date-time')}>
                      {_t('LIVE')}
                    </div>
                  )}
                  {hideChannelLinks!=='1' && <div className={bem.e('top-bar__channels')}>
                    {event.channelsInfo.map((channel: IChannel, index: number) => {
                      if(index < MAX_CHANNELS_TO_SHOW_ON_TOP_BAR) return (
                        channel.outFolder ? (
                          <ChannelTrackingLink
                            outLink={getChannelOutLink(channel.outFolder, channel.id)}
                            key={index}
                            target="_blank"
                            rel="noreferrer"
                            channelId={channel.id}
                            competitionId={event.tournamentStage.tournament.competitionId}
                          >
                            <div className={bem.e('top-bar__channels__a-channel')} key={index}>
                              {removeLocationFromChannelName(channel.name)}
                            </div>
                          </ChannelTrackingLink>
                        ) : (
                          <div className={bem.e('top-bar__channels__a-channel')} key={index}>
                            {removeLocationFromChannelName(channel.name)}
                          </div>
                        )
                      )
                      else return null
                    })}
                  </div>}
                </div>
                <div className={`${bem.e('middle-section')}`}>
                  <div className={`${bem.e('middle-section__col ')} ${bem.e('middle-section__col-home')}`}>
                    {event.eventCompetitors.filter((competitor: IEventCompetitor) => competitor.position === 1).map((competitor: IEventCompetitor, index: number) => {
                      return (
                        <div className={`${bem.e('middle-section__col--team-name')} `} key={index}>
                          <span>{competitor.competitor?.trName}</span>
                        </div>
                      )
                    })}
                    {promotedProvider !== null && filterOdds(event.maxOdds).length === 0 && !(isItalianMarket(globalState.widget?.client.regionIds) && !isItalianOddsAvailable(event.maxOdds)) && (
                      <OddsTrackingLink
                        providerId={promotedProvider.provider.id}
                        outLink={getOutLink(
                          outLinks?.outLinkFolder,
                          promotedProvider.provider.slug ?? slugify(promotedProvider.provider.name),
                          widgetId,
                          eventID ? eventID : undefined,
                          builderComponentId,
                        )}
                        widgetId={widgetId}
                        target={'_blank'}
                        rel={'noreferrer'}
                        className={bem.e('odds-btn')}
                        key={index}
                      >
                        <Odds
                          odds={null}
                          direction={null}
                          arrow={false}
                          provider={promotedProvider.provider}
                        />
                      </OddsTrackingLink>
                    )}
                    {checkIfItalianMarketAndHaveOdds(event) ?
                      <></> :
                      filterOdds(event.maxOdds).filter((oddItem: IMaxOdd) => oddItem.outcomeName === '1').map((oddItem: IMaxOdd, index: number) => {
                        return (
                          oddItem.odds && oddItem.provider ? (
                            <OddsTrackingLink
                              outLink={getOutLink(outLinks?.outLinkFolder, oddItem.provider.slug ?? slugify(oddItem.provider.name) , widgetId, eventID? eventID : undefined, builderComponentId)}
                              key={index}
                              providerId={oddItem.provider.id}
                              widgetId={widgetId}
                              target={'_blank'}
                              rel={'noreferrer'}
                              className={'CLASS_ONE'}
                            >
                              <OddsButtonWithOperatorOnly
                                oddItem={oddItem}
                                eventStatus={event.status}
                                oddsFormat={oddsFormat}
                              />
                            </OddsTrackingLink>
                          ) : (!isItalianMarket(globalState.widget?.client.regionIds) && (
                            <div className={`${bem.e('middle-section__col__odds-info--no-odds')}`} key={index}>
                              <div className={` ${bem.e('middle-section__col__odds-info--no-odds--odds')}`}>
                                -
                              </div>
                            </div>
                          )))
                      })}
                  </div>
                  <div className={`${bem.e('middle-section__col ')} ${bem.e('middle-section__col-draw')}`}>
                    <div className={`${bem.e('middle-section__col--team-name')} `}>
                      <span>{_t('DRAW')}</span>
                    </div>
                    {promotedProvider && checkOddsAvailability(event) && (
                      <OddsTrackingLink
                        outLink={getOutLink(outLinks?.outLinkFolder, promotedProvider.provider.slug ?? slugify(promotedProvider.provider.name), widgetId, eventID ? eventID : undefined, builderComponentId)}
                        key={index}
                        className={bem.e('odds-btn')}
                        target={'_blank'}
                        rel={'noreferrer'}
                        providerId={promotedProvider.provider.id}
                        widgetId={widgetId}
                      >
                        <Odds
                          odds={null}
                          direction={null}
                          arrow={false}
                          provider={promotedProvider.provider}
                        />
                      </OddsTrackingLink>
                    )}
                    {checkIfItalianMarketAndHaveOdds(event) ?
                      <></>
                      : filterOdds(event.maxOdds).filter((oddItem: IMaxOdd) => oddItem.outcomeName === 'X').map((oddItem: IMaxOdd, index: number) => {
                        return (
                          oddItem.odds && oddItem.provider ? (
                            <OddsTrackingLink
                              rel={'noreferrer'}
                              providerId={oddItem.provider.id}
                              outLink={getOutLink(outLinks?.outLinkFolder, oddItem.provider.slug ?? slugify(oddItem.provider.name), widgetId,  eventID? eventID : undefined, builderComponentId)}
                              widgetId={widgetId}
                              target={'_blank'}
                              className={'CLASS-TWO'}
                              key={index}
                            >
                              <OddsButtonWithOperatorOnly
                                oddItem={oddItem}
                                eventStatus={event.status}
                                oddsFormat={oddsFormat}
                              />
                            </OddsTrackingLink>
                          ) : (!isItalianMarket(globalState.widget?.client.regionIds) && (
                            <div className={`${bem.e('middle-section__col__odds-info--no-odds')}`} key={index}>
                              <div className={` ${bem.e('middle-section__col__odds-info--no-odds--odds')}`}>
                                -
                              </div>
                            </div>
                          )))
                      })}
                  </div>
                  <div className={`${bem.e('middle-section__col ')} ${bem.e('middle-section__col-away')}`}>
                    {event.eventCompetitors.filter((competitor: IEventCompetitor) => competitor.position === 2).map((competitor: IEventCompetitor, index: number) => {
                      return (
                        <div className={`${bem.e('middle-section__col--team-name')} `} key={index}>
                          <span>{competitor.competitor?.trName}</span>
                        </div>
                      )
                    })}
                    {promotedProvider && checkOddsAvailability(event) && (
                      <OddsTrackingLink
                        outLink={getOutLink(outLinks?.outLinkFolder, promotedProvider.provider.slug ?? slugify(promotedProvider.provider.name), widgetId, eventID ? eventID : undefined, builderComponentId)}
                        key={index}
                        className={bem.e('odds-btn')}
                        target={'_blank'}
                        rel={'noreferrer'}
                        providerId={promotedProvider.provider.id}
                        widgetId={widgetId}
                      >
                        <Odds
                          odds={null}
                          direction={null}
                          arrow={false}
                          provider={promotedProvider.provider}
                        />
                      </OddsTrackingLink>
                    )}
                    {checkIfItalianMarketAndHaveOdds(event) ?
                      <></>
                      : filterOdds(event.maxOdds).filter((oddItem: IMaxOdd) => oddItem.outcomeName === '2').map((oddItem: IMaxOdd, index: number) => {
                        return (
                          oddItem.odds && oddItem.provider ? (
                            <OddsTrackingLink
                              outLink={getOutLink(outLinks?.outLinkFolder, oddItem.provider.slug ?? slugify(oddItem.provider.name) , widgetId,  eventID? eventID : undefined, builderComponentId)}
                              key={index}
                              target={'_blank'}
                              rel={'noreferrer'}
                              providerId={oddItem.provider.id}
                              widgetId={widgetId}
                            >
                              <OddsButtonWithOperatorOnly
                                oddItem={oddItem}
                                eventStatus={event.status}
                                oddsFormat={oddsFormat}
                              />
                            </OddsTrackingLink>
                          ) : (!isItalianMarket(globalState.widget?.client.regionIds) && (
                            <div className={`${bem.e('middle-section__col__odds-info--no-odds')}`} key={index}>
                              <div className={` ${bem.e('middle-section__col__odds-info--no-odds--odds')}`}>
                                -
                              </div>
                            </div>
                          )))
                      })}
                  </div>
                </div>
                <div className={`${bem.e('bottom-bar')}`}>
                  <div className={`${bem.e('bottom-bar__competition')}`}>
                    {event.tournamentStage.tournament.competition.name}
                  </div>
                  {featuredEventsData.tournament.competition.sport?.name
                    && featuredEventsData.tournament.competition.region?.name && linkTemplate
                    && (broadcastLinksDisabled === undefined || broadcastLinksDisabled.value === '0')
                    && (
                      <a
                        href={getEventLink(
                          linkTemplate,
                          event.id,
                          slugify(event.name),
                          slugify(featuredEventsData.tournament.competition.sport.name),
                          slugify(featuredEventsData.tournament.competition.name),
                          slugify(featuredEventsData.tournament.competition.region.name),
                          slugify(featuredEventsData.tournament.competition.region.trName),
                          slugify(featuredEventsData.tournament.competition.sport.trName),
                        )}
                        rel="noreferrer"
                        target='_blank'
                      >
                        <div className={`${bem.e('bottom-bar__view-button')}`}>
                          {_t('View Match')} {'❯'}
                        </div>
                      </a>
                    )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
export default FeaturedEvents;
